import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
import { fetchAddons } from "../../services/addonService";
import { readStoredPromo } from "../../services/promotionService";
import { useTranslation } from "../../hooks/useTranslation";
import { MEDIA_URL } from "../../config/constants";

// Work out what an applied promo is worth for this booking.
//
// This mirrors eligibleBase()/computeDiscount() in the backend's
// promotion_service.go so the figure shown here is the figure charged. The
// server remains authoritative and recalculates on order creation; this is a
// display of the same rules, not a second source of truth.
const calculatePromoDiscount = (promo, { rentalBase, addOnsTotal, bookingTotal }) => {
  if (!promo) return { promo: null, discount: 0 };

  let base;
  switch (promo.appliesTo) {
    case "rental_and_addons":
      base = rentalBase + addOnsTotal;
      break;
    case "booking_total":
      base = bookingTotal;
      break;
    default: // rental_base
      base = rentalBase;
  }

  const value = Number(promo.discountValue) || 0;
  let discount =
    promo.discountType === "percentage" ? (base * value) / 100
    : promo.discountType === "fixed_amount" ? value
    : 0;

  // A cap is applied server-side; honour it here when the payload carries one.
  const cap = Number(promo.maxDiscountAmount) || 0;
  if (cap > 0 && discount > cap) discount = cap;

  // Never discount more than the eligible base.
  if (discount > base) discount = base;
  if (!(discount > 0)) return { promo: null, discount: 0 };

  return { promo, discount: Math.round(discount * 100) / 100 };
};

// Parse YYYY-MM-DD string as local date (not UTC)
const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;
  // If it's already a Date object or ISO string with time, use Date constructor
  if (dateStr instanceof Date) return dateStr;
  // For YYYY-MM-DD format, parse as local date to avoid timezone issues
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    return new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
  }
  // Fallback to Date constructor for other formats
  return new Date(dateStr);
};

const BookingSummary = () => {
  const location = useLocation();
  const { t } = useTranslation('checkout');
  const [bookingData, setBookingData] = useState({
    vanTitle: "Wave Catchers",
    vanImage:
      `${MEDIA_URL}/images/placeholders/campervan-placeholder.jpg`,
    basePrice: 80.46,
    nights: 6,
    startDate: null,
    endDate: null,
    pickupLocation: null,
    dropoffLocation: null,
    pickupTime: null,
    dropoffTime: null,
    priceBreakdown: null,
    totalPrice: 0,
    adults: 1,
    children: 0,
    pets: false,
    numberOfPets: 0,
    selectedInsurance: null,
    addOns: {},
    addOnQuantities: {},
    paymentOption: "deposit",
  });

  // State for expandable sections
  const [expandedSections, setExpandedSections] = useState({
    vanRental: true,
    insurance: false,
    pets: false,
    addOns: false,
    taxesFees: false,
  });

  // State for add-ons from API
  const [availableAddOns, setAvailableAddOns] = useState([]);

  // Fetch add-ons from API
  useEffect(() => {
    const loadAddOns = async () => {
      try {
        const addonsData = await fetchAddons();
        const transformedAddons = addonsData.map(addon => {
          const pricingTypeName = addon.PricingTypeName?.String || addon.PricingTypeName || '';
          const dailyRate = parseFloat(addon.DailyRate?.String || '0');
          const flatRate = parseFloat(addon.FlatRate?.String || '0');

          return {
            id: addon.ID,
            name: addon.Name?.String || addon.Name || '',
            description: addon.Description?.String || addon.Description || '',
            dailyRate: dailyRate,
            flatRate: flatRate,
            pricingType: pricingTypeName.toLowerCase(), // "daily" or "flat_rate"
            category: addon.CategoryName?.String || addon.CategoryName || "Miscellaneous",
            hasQuantity: (addon.MaxQuantity?.Int32 || addon.MaxQuantity || 1) > 1
          };
        });
        setAvailableAddOns(transformedAddons);
      } catch (error) {
        console.error('Error loading add-ons:', error);
        // Fallback to empty array
        setAvailableAddOns([]);
      }
    };

    loadAddOns();
  }, []);

  // Determine current checkout step
  const getCurrentStep = () => {
    const path = location.pathname;
    if (path.includes('/checkout/step1')) return 1;
    if (path.includes('/checkout/step2')) return 2;
    if (path.includes('/checkout/step3')) return 3;
    return 0; // camper details or other pages
  };

  // Update expanded sections based on current step
  useEffect(() => {
    const currentStep = getCurrentStep();

    // Track checkout step in Google Analytics
    ReactGA.event({
      category: "Ecommerce",
      action: `Checkout Step ${currentStep}`,
    });

    // Set expansion based on step
    const newExpansion = {
      vanRental: currentStep === 0 || currentStep === 1, // Always expanded on camper details and step 1
      insurance: currentStep === 2, // Expanded on step 2 (insurance selection)
      addOns: currentStep === 3, // Expanded on step 3 (add-ons)
      taxesFees: currentStep === 3, // Also expanded on step 3 for final review
    };
    
    setExpandedSections(newExpansion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Toggle section expansion
  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Load data from sessionStorage and listen for changes
  useEffect(() => {
    const loadBookingData = () => {
      // Load from campervan booking (from EnhancedBookingForm)
      const camperBooking = sessionStorage.getItem("bookingData");
      // Load from checkout step 1 (passenger info)
      const step1Data = sessionStorage.getItem("checkoutStep1Data");
      // Load from checkout step 2 (insurance)
      const step2Data = sessionStorage.getItem("checkoutStep2Data");

      let updatedData = { ...bookingData };

      if (camperBooking) {
        try {
          const parsed = JSON.parse(camperBooking);
          updatedData = {
            ...updatedData,
            vanTitle: parsed.vanTitle || updatedData.vanTitle,
            vanImage: parsed.vanImage || updatedData.vanImage,
            basePrice: parsed.basePrice || updatedData.basePrice,
            nights: parsed.nights || updatedData.nights,
            startDate: parseLocalDate(parsed.startDate),
            endDate: parseLocalDate(parsed.endDate),
            pickupLocation: parsed.pickupLocation || null,
            dropoffLocation: parsed.dropoffLocation || null,
            pickupTime: parsed.pickupTime || null,
            dropoffTime: parsed.dropoffTime || null,
            priceBreakdown: parsed.priceBreakdown || null,
            totalPrice: parsed.totalPrice || updatedData.totalPrice,
          };
        } catch (e) {
          console.error("Error parsing camper booking data:", e);
        }
      }

      if (step1Data) {
        try {
          const parsed = JSON.parse(step1Data);
          updatedData = {
            ...updatedData,
            adults: parseInt(parsed.adults) || 1,
            children: parseInt(parsed.children) || 0,
            pets: parsed.pets || false,
            numberOfPets: parseInt(parsed.numberOfPets) || 0,
          };
        } catch (e) {
          console.error("Error parsing step1 data:", e);
        }
      }

      if (step2Data) {
        try {
          const parsed = JSON.parse(step2Data);
          updatedData = {
            ...updatedData,
            selectedInsurance: parsed.selectedPlan,
          };
        } catch (e) {
          console.error("Error parsing step2 data:", e);
        }
      }

      // Load from checkout step 3 (add-ons)
      const step3Data = sessionStorage.getItem("checkoutStep3Data");
      if (step3Data) {
        try {
          const parsed = JSON.parse(step3Data);
          updatedData = {
            ...updatedData,
            addOns: parsed.selectedAddOns || {},
            addOnQuantities: parsed.quantities || {},
          };
        } catch (e) {
          console.error("Error parsing step3 data:", e);
        }
      }

      // How much the customer chose to pay online: 'deposit' (50%) or 'full'.
      updatedData.paymentOption =
        sessionStorage.getItem("checkoutPaymentOption") || "deposit";

      setBookingData(updatedData);
    };

    // Load initial data
    loadBookingData();

    // Listen for localStorage changes
    const handleStorageChange = () => {
      loadBookingData();
    };

    window.addEventListener("storage", handleStorageChange);

    // Also listen for custom events when localStorage is updated from same tab
    const handleBookingUpdate = () => {
      loadBookingData();
    };

    window.addEventListener("bookingUpdated", handleBookingUpdate);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("bookingUpdated", handleBookingUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate pricing
  const calculatePricing = () => {
    // Use the breakdown from EnhancedBookingForm if available
    let baseTotal = bookingData.basePrice * bookingData.nights;
    let baseDiscount = 0;
    let baseDiscountRate = 0;
    let discountedBase = baseTotal;
    let pickupFee = 0;
    let dropoffFee = 0;
    let timeFees = 0;

    if (bookingData.priceBreakdown) {
      baseTotal = bookingData.priceBreakdown.dailyTotal;
      baseDiscount = bookingData.priceBreakdown.baseDiscount || 0;
      baseDiscountRate = bookingData.priceBreakdown.baseDiscountRate || 0;
      discountedBase = bookingData.priceBreakdown.discountedBase || (baseTotal - baseDiscount);
      pickupFee = bookingData.priceBreakdown.pickupFee;
      dropoffFee = bookingData.priceBreakdown.dropoffFee;
      timeFees = bookingData.priceBreakdown.timeFees;
    }

    // Insurance pricing - matches database
    const insurancePrices = {
      basic: 0,
      premium: 17,
      ultimate: 23,
    };
    const insuranceTotal = bookingData.selectedInsurance
      ? insurancePrices[bookingData.selectedInsurance] * bookingData.nights
      : 0;

    // Pets cleaning fee (€50 per rental for pets)
    const petsFee = bookingData.pets && bookingData.numberOfPets > 0 ? 50 : 0;

    // Create add-on details map from API data
    const addOnDetails = {};
    availableAddOns.forEach(addon => {
      addOnDetails[addon.id] = {
        name: addon.name,
        dailyRate: addon.dailyRate || 0,
        flatRate: addon.flatRate || 0,
        pricingType: addon.pricingType || 'flat_rate',
        hasQuantity: addon.hasQuantity
      };
    });

    let addOnsTotal = 0;
    const selectedAddOnsList = [];

    Object.keys(bookingData.addOns).forEach((addOnId) => {
      if (bookingData.addOns[addOnId]) {
        const addOn = addOnDetails[addOnId];
        if (addOn) {
          const quantity = bookingData.addOnQuantities[addOnId] || 1;

          // Calculate item total based on pricing type
          let itemTotal = 0;
          let displayPrice = 0;

          if (addOn.pricingType === 'daily' && addOn.dailyRate > 0) {
            // Daily rate: price per night * number of nights * quantity
            displayPrice = addOn.dailyRate * bookingData.nights;
            itemTotal = displayPrice * quantity;
          } else {
            // Flat rate: one-time price * quantity
            displayPrice = addOn.flatRate;
            itemTotal = addOn.flatRate * quantity;
          }

          addOnsTotal += itemTotal;

          selectedAddOnsList.push({
            id: addOnId,
            name: addOn.name,
            price: displayPrice,
            quantity: quantity,
            total: itemTotal,
            hasQuantity: addOn.hasQuantity,
            pricingType: addOn.pricingType
          });
        }
      }
    });

    // All prices already include 23% Portugal VAT
    // Use discountedBase instead of baseTotal to apply stay-length discount
    const totalBeforePromo =
      discountedBase +
      pickupFee +
      dropoffFee +
      timeFees +
      insuranceTotal +
      petsFee +
      addOnsTotal;

    // discountedBase, not baseTotal: the server discounts against the
    // stay-length-discounted rental, so this must too.
    const { promo: appliedPromo, discount: promoDiscount } = calculatePromoDiscount(
      readStoredPromo(),
      { rentalBase: discountedBase, addOnsTotal, bookingTotal: totalBeforePromo }
    );

    const total = Math.max(0, totalBeforePromo - promoDiscount);

    // Extract the 23% VAT from the total (prices already include it)
    // Formula: tax = total * (0.23 / 1.23)
    const tax = total * 0.23 / 1.23;
    // subtotal not currently displayed but kept for reference: total - tax

    // What is paid online now. On 'deposit' that is 50% of the rental — add-ons
    // are settled on-site so they stay out of the deposit base, and the rest is
    // paid at pickup. On 'full' the customer pays everything up front, add-ons
    // included, and nothing is left owing.
    const deposit =
      bookingData.paymentOption === "full" ? total : (total - addOnsTotal) * 0.5;
    const balanceDue = total - deposit;

    return {
      baseTotal,
      baseDiscount,
      baseDiscountRate,
      discountedBase,
      pickupFee,
      dropoffFee,
      timeFees,
      insuranceTotal,
      petsFee,
      addOnsTotal,
      selectedAddOnsList,
      appliedPromo,
      promoDiscount,
      totalBeforePromo,
      tax,
      total,
      deposit,
      balanceDue,
    };
  };

  const pricing = calculatePricing();

  const getInsuranceName = (plan) => {
    const names = {
      basic: "Basic Insurance",
      premium: "Premium Insurance",
      ultimate: "Ultimate Insurance",
    };
    return names[plan] || "Insurance Plan";
  };

  useEffect(() => {
    const pricing = calculatePricing();
    const currentStep = getCurrentStep();

    ReactGA.event({
      category: "Ecommerce",
      action: `Checkout Step ${currentStep}`,
      value: pricing.total, // Total price at this step
      ecommerce: {
        items: pricing.selectedAddOnsList.map(addOn => ({
          id: addOn.id,
          name: addOn.name,
          price: addOn.price,
          quantity: addOn.quantity,
        })),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <div className="booking-summary">
      <div className="booking-details">
        <h2 className="van-title">{bookingData.vanTitle}</h2>
        <img
          src={bookingData.vanImage}
          alt={`${bookingData.vanTitle} Campervan`}
          className="van-image"
        />

        <div className="divider"></div>

        <div className="booking-dates">
          <div className="date-section">
            <div className="location">
              {bookingData.startDate
                ? bookingData.startDate.toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })
                : "Pick-up Date"}
            </div>
            <div className="date">
              {bookingData.pickupLocation?.name || "Pick-up Location"}
            </div>
            {bookingData.pickupTime && (
              <div className="date-time">
                Pickup at {bookingData.pickupTime.time}
              </div>
            )}
          </div>
          <div className="date-separator">To</div>
          <div className="date-section">
            <div className="location">
              {bookingData.endDate
                ? bookingData.endDate.toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })
                : "Drop-off Date"}
            </div>
            <div className="date">
              {bookingData.dropoffLocation?.name || "Drop-off Location"}
            </div>
            {bookingData.dropoffTime && (
              <div className="date-time">
                Drop-off at {bookingData.dropoffTime.time}
              </div>
            )}
          </div>
        </div>

        <div className="divider"></div>

        <div className="price-breakdown-section">
          <h3 className="price-breakdown-title">Price Breakdown</h3>
        </div>

        <div className="price-breakdown">
          {/* VAN RENTAL SECTION */}
          <div className="price-section-header" onClick={() => toggleSection('vanRental')}>
            <div className="section-header-content">
              <span className="section-title">Van Rental</span>
              <span className="expand-icon">
                {expandedSections.vanRental ? '−' : '+'}
              </span>
            </div>
            <span className="section-total">
              €
              {(
                pricing.discountedBase +
                pricing.pickupFee +
                pricing.dropoffFee +
                pricing.timeFees
              ).toFixed(2)}
            </span>
          </div>
          {expandedSections.vanRental && (
            <div className="section-details">
              <div className="price-item price-sub-item">
                <span className="price-description">
                  €{bookingData.priceBreakdown?.dailyRate
                    ? bookingData.priceBreakdown.dailyRate.toFixed(2)
                    : bookingData.basePrice.toFixed(2)} × {bookingData.nights} nights
                </span>
                <span className="price-value">€{pricing.baseTotal.toFixed(2)}</span>
              </div>
              {pricing.baseDiscount > 0 && (
                <div className="price-item price-sub-item discount-item">
                  <span className="price-description discount-label">
                    Stay-length discount ({Math.round(pricing.baseDiscountRate * 100)}% off)
                  </span>
                  <span className="price-value discount-value">
                    -€{pricing.baseDiscount.toFixed(2)}
                  </span>
                </div>
              )}
              <div className="price-item price-sub-item">
                <span className="price-description">
                  Pick-up ({bookingData.pickupLocation?.name || "Location"})
                </span>
                <span className="price-value">
                  {pricing.pickupFee > 0
                    ? `€${pricing.pickupFee.toFixed(2)}`
                    : "Included"}
                </span>
              </div>
              <div className="price-item price-sub-item">
                <span className="price-description">
                  Drop-off ({bookingData.dropoffLocation?.name || "Location"})
                </span>
                <span className="price-value">
                  {pricing.dropoffFee > 0
                    ? `€${pricing.dropoffFee.toFixed(2)}`
                    : "Included"}
                </span>
              </div>
              {pricing.timeFees > 0 && (
                <div className="price-item price-sub-item">
                  <span className="price-description">Time slot fees</span>
                  <span className="price-value">
                    €{pricing.timeFees.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* INSURANCE SECTION */}
          <div className="price-section-header" onClick={() => toggleSection('insurance')}>
            <div className="section-header-content">
              <span className="section-title">Insurance</span>
              <span className="expand-icon">
                {expandedSections.insurance ? '−' : '+'}
              </span>
            </div>
            <span className="section-total">
              {bookingData.selectedInsurance
                ? `€${pricing.insuranceTotal.toFixed(2)}`
                : "-"}
            </span>
          </div>
          {expandedSections.insurance && (
            <div className="section-details">
              {bookingData.selectedInsurance ? (
                <div className="price-item price-sub-item">
                  <span className="price-description">
                    {getInsuranceName(bookingData.selectedInsurance)} (
                    {bookingData.nights} days)
                  </span>
                  <span className="price-value">
                    €{pricing.insuranceTotal.toFixed(2)}
                  </span>
                </div>
              ) : (
                <div className="price-item price-sub-item">
                  <span className="price-description">No insurance selected</span>
                  <span className="price-value">-</span>
                </div>
              )}
            </div>
          )}

          {/* PETS SECTION */}
          {bookingData.pets && (
            <div className="price-section-header" onClick={() => toggleSection('pets')}>
              <div className="section-header-content">
                <span className="section-title">Pets</span>
                <span className="expand-icon">
                  {expandedSections.pets ? '−' : '+'}
                </span>
              </div>
              <span className="section-total">
                €{pricing.petsFee.toFixed(2)}
              </span>
            </div>
          )}
          {expandedSections.pets && bookingData.pets && (
            <div className="section-details">
              <div className="price-item price-sub-item">
                <span className="price-description">
                  Cleaning fee ({bookingData.numberOfPets} pet{bookingData.numberOfPets > 1 ? 's' : ''})
                </span>
                <span className="price-value">
                  €{pricing.petsFee.toFixed(2)}
                </span>
              </div>
            </div>
          )}

          {/* ADD-ONS SECTION */}
          <div className="price-section-header" onClick={() => toggleSection('addOns')}>
            <div className="section-header-content">
              <span className="section-title">Add-ons</span>
              <span className="expand-icon">
                {expandedSections.addOns ? '−' : '+'}
              </span>
            </div>
            <span className="section-total">
              {pricing.addOnsTotal > 0
                ? `€${pricing.addOnsTotal.toFixed(2)}`
                : "-"}
            </span>
          </div>
          {expandedSections.addOns && (
            <div className="section-details">
              {pricing.selectedAddOnsList.length > 0 ? (
                pricing.selectedAddOnsList.map((addOn) => (
                  <div key={addOn.id} className="price-item price-sub-item">
                    <span className="price-description">
                      {addOn.name}
                      {addOn.hasQuantity && addOn.quantity > 1
                        ? ` (×${addOn.quantity})`
                        : ""}
                    </span>
                    <span className="price-value">€{addOn.total.toFixed(2)}</span>
                  </div>
                ))
              ) : (
                <div className="price-item price-sub-item">
                  <span className="price-description">No add-ons selected</span>
                  <span className="price-value">-</span>
                </div>
              )}
            </div>
          )}
        </div>

        {pricing.promoDiscount > 0 && (
          <div className="price-item discount-item promo-item">
            <span className="price-description discount-label">
              Promo code {pricing.appliedPromo?.code}
              {pricing.appliedPromo?.discountType === "percentage" &&
                ` (${Number(pricing.appliedPromo.discountValue)}% off)`}
            </span>
            <span className="price-value discount-value">
              -€{pricing.promoDiscount.toFixed(2)}
            </span>
          </div>
        )}

        <div className="divider"></div>

        <div className="total-price">
          <span className="price-description">Total Price</span>
          <span className="total-value">€{pricing.total.toFixed(2)}</span>
        </div>

        <div className="deposit-breakdown">
          <div className="deposit-row deposit-now">
            <span className="deposit-label">{t('summary.depositNow')}</span>
            <span className="deposit-value">€{pricing.deposit.toFixed(2)}</span>
          </div>
          <div className="deposit-row deposit-later">
            <span className="deposit-label">{t('summary.depositLater')}</span>
            <span className="deposit-value">€{pricing.balanceDue.toFixed(2)}</span>
          </div>
        </div>

        <div className="tax-included-note">
          <span className="tax-note-text">Includes 23% VAT (€{pricing.tax.toFixed(2)})</span>
        </div>
      </div>
    </div>
  );
};

export default BookingSummary;
